/**
 * Copyright (c) [2026] SUSE Linux
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import { apiGetUserInfo } from "../../api/member";

const STORAGE_KEY = 'jangouts-user-store';

// Action types
const SET_USER_LOGIN = 'jangouts/user/SET_LOGIN';
const UPDATE_USER_INFO = 'jangouts/user/UPDATE_INFO';
const RESET_AUTHORIZED = 'jangouts/user/RESET';
const SET_TOKEN = 'jangouts/user/SET_TOKEN';

// Initial state
const initialState = {
  isLogin: false,
  token: "",
  "refresh-token": "",
  userInfo: {
    userId: 0,
    avatar: "",
    nickname: "",
    username: ""
  }
};

// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const serialized = localStorage.getItem(STORAGE_KEY);
    if (serialized === null) return initialState;
    const parsed = JSON.parse(serialized);
    // Merge with initialState to ensure any new fields are present
    return { ...initialState, ...parsed };
  } catch (err) {
    console.warn('Failed to load persisted user state:', err);
    return initialState;
  }
};

// Save state to localStorage
const savePersistedState = (state) => {
  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch (err) {
    console.warn('Failed to save persisted user state:', err);
  }
};

// Action creators
export const setUserLogin = (loginData) => {
  return {
    type: SET_USER_LOGIN,
    payload: loginData
  };
};

export const updateUserInfo = (userInfo) => {
  return {
    type: UPDATE_USER_INFO,
    payload: userInfo
  };
};

export const resetAuthorized = () => {
  return {
    type: RESET_AUTHORIZED
  };
};

export const setToken = (token, refreshToken) => {
  return {
    type: SET_TOKEN,
    payload: { token, refreshToken }
  };
};

// Thunk action: loginAfter
export const loginAfter = () => {
  return async (dispatch) => {
    try {
      const res = await apiGetUserInfo();
      dispatch(updateUserInfo({
        avatar: res.data.avatar,
        nickname: res.data.nickname,
        username: res.data.username
      }));
    } catch (error) {
      console.error('Failed to fetch user info after login:', error);
    }
  };
};

const actionCreators = {
  setUserLogin,
  updateUserInfo,
  resetAuthorized,
  setToken,
  loginAfter
};

const actionTypes = {
  SET_USER_LOGIN,
  UPDATE_USER_INFO,
  RESET_AUTHORIZED,
  SET_TOKEN
};

// Reducer
const reducer = (state = loadPersistedState(), action) => {
  let newState;
  switch (action.type) {
    case SET_USER_LOGIN: {
      const { isLogin = true, token, refreshToken, userId } = action.payload;
      newState = {
        ...state,
        isLogin,
        token: token || state.token,
        "refresh-token": refreshToken || state["refresh-token"],
        userInfo: {
          ...state.userInfo,
          userId: userId !== undefined ? userId : state.userInfo.userId
        }
      };
      break;
    }
    case UPDATE_USER_INFO: {
      newState = {
        ...state,
        userInfo: {
          ...state.userInfo,
          ...action.payload
        }
      };
      break;
    }
    case RESET_AUTHORIZED: {
      newState = { ...initialState };
      break;
    }
    case SET_TOKEN: {
      const { token, refreshToken } = action.payload;
      newState = {
        ...state,
        token: token || state.token,
        "refresh-token": refreshToken || state["refresh-token"]
      };
      break;
    }
    default:
      return state;
  }

  // Persist state after each update
  savePersistedState(newState);
  return newState;
};

export { actionCreators, actionTypes, initialState };

export default reducer;